import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { Team } from "../../types/team";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import { TeamMember } from "../../types/teamMember";
import { Person } from "../../types/person";
import useTeamMemberQuery from "../../hooks/query/connections/useTeamMemberQuery";
import useTeamMemberLogic from "../../hooks/logic/connections/teamMemberLogic";

interface TeamMemberController {
  from?: Team | null;
  to?: Person | null;
  systemLanguageType: TeamMember["system_language_type"];
}

const useTeamMemberController = ({
  from = null,
  to = null,
  systemLanguageType,
}: TeamMemberController): ConnectionFormDataController<
  TeamMember,
  Team,
  Person
> => {
  if ((from === null && to === null) || (from !== null && to !== null)) {
    throw new Error("Exactly one of 'from' or 'to' must be specified");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity: Team | Person = (from || to)!;
  const { useFetchById, useUpdate, useCreate } =
    useTeamMemberQuery(systemLanguageType);
  const { userEnums, getDisplayName, defaultTeamMember } = useTeamMemberLogic(
    from,
    to,
    systemLanguageType
  );
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const [isDialogShown, setShowDialog] = useState<boolean>(false);

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (id: string) => {
    setId(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<TeamMember> => {
    if (id === null) {
      return defaultTeamMember;
    }
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Team Member Relation data not found");
    }
    return connectionData;
  };

  const onSubmit = async (data: Partial<TeamMember>) => {
    const metadata: Partial<Omit<TeamMember, "_from" | "_to">> = {
      notes: data.notes,
      internal_notes: data.internal_notes,
      role: data.role,
      status: data.status,
      name_external_evidence: data.name_external_evidence,
      name_internal_evidence: data.name_internal_evidence,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      attributes_metadata: data.attributes_metadata,
    };
    if (isCreateMode) {
      metadata.system_language_type = systemLanguageType;
    }
    const updateData: CreateEdgeData<TeamMember> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Team Member Relation");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Team Member Relation");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;

  const title = (data: TeamMember) =>
    `${isCreateMode ? "Create New" : "Edit"} ${getDisplayName(data)}`;

  return {
    defaultValues: defaultTeamMember,
    isCreateMode,
    userEnums,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};

export default useTeamMemberController;
